import { BorderStyle, convertInchesToTwip } from 'docx';

// Tokens
import theme from '../../tokens';

// Utils
import {
  convertPtToHalfPt,
  convertPtToPx,
  convertPtToTwip,
  convertHexToDocx,
  convertLineHeight,
} from '@/components/template/resume/utils/converters';

export const FONT_FAMILY = theme.typography.fontFamily;

/** Docx-compatible hex colors from Default design tokens */
export const COLORS = {
  primary: convertHexToDocx(theme.colors.primary),
  foreground: convertHexToDocx(theme.colors.foreground),
  border: convertHexToDocx(theme.colors.border),
  surface: convertHexToDocx(theme.colors.surface),
  white: convertHexToDocx(theme.colors.background),
  blockquoteBorder: '888888',
} as const;

export const FONT_SIZES = {
  '6xl': convertPtToHalfPt(theme.typography.fontSize['6xl']),
  '5xl': convertPtToHalfPt(theme.typography.fontSize['5xl']),
  '4xl': convertPtToHalfPt(theme.typography.fontSize['4xl']),
  '3xl': convertPtToHalfPt(theme.typography.fontSize['3xl']),
  '2xl': convertPtToHalfPt(theme.typography.fontSize['2xl']),
  xl: convertPtToHalfPt(theme.typography.fontSize.xl),
  lg: convertPtToHalfPt(theme.typography.fontSize.lg),
  base: convertPtToHalfPt(theme.typography.fontSize.base),
  sm: convertPtToHalfPt(theme.typography.fontSize.sm),
  xs: convertPtToHalfPt(theme.typography.fontSize.xs),
} as const;

/** A4 page size in twips */
export const A4 = {
  width: convertInchesToTwip(8.27),
  height: convertInchesToTwip(11.69),
} as const;

export const SIDEBAR_WIDTH = convertInchesToTwip(
  theme.spacing.sidebarWidth / 72,
);
export const PAGE_PADDING = convertInchesToTwip(theme.spacing.pagePadding / 72);

/** Contact icon size in px derived from theme token */
export const ICON_SIZE = convertPtToPx(theme.spacing.iconSize);

/** Avatar size in px */
export const AVATAR_SIZE_PX = convertPtToPx(theme.spacing.avatarSize);

/** Logo dimensions in px */
export const LOGO_WIDTH_PX = convertPtToPx(theme.spacing.logoWidth);
export const LOGO_HEIGHT_PX = convertPtToPx(theme.spacing.logoHeight);

export const NO_BORDER = {
  top: { style: BorderStyle.NONE, size: 0, color: 'FFFFFF' },
  bottom: { style: BorderStyle.NONE, size: 0, color: 'FFFFFF' },
  left: { style: BorderStyle.NONE, size: 0, color: 'FFFFFF' },
  right: { style: BorderStyle.NONE, size: 0, color: 'FFFFFF' },
} as const;

export const THIN_BORDER_BOTTOM = {
  ...NO_BORDER,
  bottom: { style: BorderStyle.SINGLE, size: 0.5, color: COLORS.border },
} as const;

/** Line spacing: 240 × lineHeight (in 240ths of a line) */
export const LINE_SPACING = convertLineHeight(theme.typography.lineHeight);

/** Pre-computed spacing values in twips derived from theme tokens */
export const SPACINGS = {
  /** Derived from theme.spacing */
  bulletBefore: convertPtToTwip(theme.spacing.bulletBefore),
  small: convertPtToTwip(theme.spacing.small),
  socialBefore: convertPtToTwip(theme.spacing.socialGap),
  medium: convertPtToTwip(theme.spacing.medium),
  large: convertPtToTwip(theme.spacing.large),
  sectionBottom: convertPtToTwip(theme.spacing.sectionBottom),
  schoolAfter: convertPtToTwip(theme.spacing.schoolAfter),
  sectionHeading: convertPtToTwip(theme.spacing.sectionHeadingMargin),

  /** DOCX-only spacing (no PDF equivalent) */
  micro: convertPtToTwip(1),
  roleAfter: convertPtToTwip(3.5),
  sectionDividerGap: convertPtToTwip(8),
  blockquoteIndent: convertPtToTwip(8),
  bulletIndent: convertPtToTwip(theme.spacing.small),

  /** Tighter line height for skill items (240 × 1.25) */
  skillLine: convertLineHeight(1.25),

  /** DOCX table cell margins derived from PDF card padding tokens */
  cardMarginY: convertPtToTwip(theme.spacing.cardPaddingY),
  cardMarginX: convertPtToTwip(theme.spacing.cardPaddingX),
} as const;
